import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn, useFieldArray, useWatch, FieldValues } from 'react-hook-form';
import { useTranslation } from '@/hooks/useTranslation';
import { Link } from 'react-router-dom';
import { useOrgBasePath } from '@/hooks/useOrgNavigate';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Checkbox } from '@/components/ui/checkbox';
import { AccountSelect, type AccountOption } from '@/components/accounting/ui/account-select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Trash2, Info, AlertTriangle } from 'lucide-react';
import { formatCurrency, getCurrencyLocale, formatOrganizationCurrency } from '@/lib/currency';
import { useOrganization } from '@/contexts/OrganizationContext';
import { LineItemCarbon } from '@/components/carbon/LineItemCarbon';
import { CarbonData } from '@/hooks/useCarbonTracking';
import {
  useJurisdictionTaxConfig,
  TaxRate,
  taxRateToSelectValue,
  selectValueToTaxRate,
  selectValueToTaxRateId,
  taxRateIdOf,
  uniqueRateAtPercentage,
  COMPUTED_RATE_HINT_TOLERANCE,
  type TaxDirection,
} from '@/hooks/useJurisdictionTaxConfig';
import { isRateEffectiveOn, rateInForce } from '@releve/shared';
import { formatLocalISODate } from '@/lib/dateUtils';
import {
  amountChangeForRate,
  isUnresolvedScanMiss,
  notReclaimableOptionLabel,
  SCAN_RATE_REQUIRED,
  SCAN_RATE_REQUIRED_ZERO_ONLY,
  zeroRateOptionLabel,
} from '@/lib/taxRateScan';
import { windowLabel } from '@/lib/taxRateWindowLabel';
import { NumberDisplay } from '@/components/ui/number-display';
import { LineItemCustomRateDialog } from '@/components/accounting/LineItemCustomRateDialog';
import {
  calculatePreTaxUnitPrice,
  calculateLineSubtotal,
  calculateLineTax,
  calculateLineTotalWithTax,
} from '@/lib/taxCalculations';
import { getAccountSignForForm } from '@/lib/account-category-utils';
import { useRenderDebug } from '@/hooks/useRenderDebug';
import { BudgetCategoryPicker } from '@/components/budgets/BudgetCategoryPicker';
import { WkrCategorySelect } from '@/components/tax/nl/wkr/WkrCategorySelect';
import { WkrLineToggle } from '@/components/tax/nl/wkr/WkrLineToggle';

import type { TaxRateType } from '@/lib/taxCalculations';
export type { TaxRateType };

export type InvoiceLineItemType = 'ar' | 'ap';

interface BaseLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: TaxRateType;
  tax_rate_id?: string;
  tax_inclusive: boolean;
  is_reverse_charge: boolean;
  exemption_reason?: string;
  exemption_reason_other?: string;
  /**
   * Placeholder 0 awaiting the org default. Set only by placeholder creators;
   * hydrators (`toTaxRateType` callers, recurringPayload) never set it.
   * Form-only; stripped by every submit whitelist.
   */
  tax_rate_unset?: boolean;
  /**
   * The percentage a scanned document printed (or, with
   * `tax_rate_hint_computed`, that its totals imply) for this line. The fill
   * pass picks the ONE rate at that percentage; with none (or a tie) the line
   * stays at 0% and the hint is shown as "Scanned: N%" — never snapped to a
   * near rate. 0 / absent = no hint. Form-only; stripped by every submit
   * whitelist.
   */
  tax_rate_hint?: number;
  /** The hint was computed from totals (tax ÷ subtotal): matched within ±0.01. */
  tax_rate_hint_computed?: boolean;
  /** The scanned VAT amount for the line, when printed (form-only; see grossUpForNotReclaimable). */
  tax_rate_hint_vat?: number;
  tax_rate_hint_vat_base?: number;
  /** The net unit price while the line is grossed up for "VAT not reclaimable" (form-only). */
  tax_rate_hint_net?: number;
  /** The unit price the "VAT not reclaimable" gross-up wrote (form-only). */
  tax_rate_hint_gross?: number;
  /** The user stated the amount after a gross-up: never gross it up again (form-only). */
  tax_rate_hint_amount_kept?: boolean;
  /**
   * The user picked in the tax picker ("No VAT" included). A scanned line
   * whose hint matched no rate blocks submit until then (lib/taxRateScan.ts).
   * Form-only; stripped by every submit whitelist.
   */
  tax_rate_confirmed?: boolean;
}

interface ARLineItem extends BaseLineItem {
  revenue_account_id: string;
}

interface APLineItem extends BaseLineItem {
  expense_account_id: string;
}

type Account = AccountOption;

interface InvoiceLineItemsProps {
  type: InvoiceLineItemType;
  form: UseFormReturn<FieldValues>;
  accounts?: Account[]; // For AP: expense accounts
  revenueAccounts?: Account[]; // For AR: revenue accounts
  disabled?: boolean; // Disable all inputs
  currency?: string | null; // Override currency for amounts (e.g., from AR/AP account)
  // Carbon tracking (optional)
  carbonDataByLineItem?: Record<number, CarbonData | null>;
  onCarbonDataChange?: (index: number, data: CarbonData | null) => void;
  /** Optional content to render on the left side of the totals summary */
  summaryLeftContent?: React.ReactNode;
  /** Callback when any line item has reverse charge applied - used to show RC notice */
  onHasReverseCharge?: (hasRC: boolean) => void;
  /** Callback with tax-related notes to add to customer memo (reverse charge text, exemption reasons) */
  onTaxNotesChange?: (notes: string) => void;
  /** Account IDs to pin at the top of line item account dropdowns */
  pinnedAccountIds?: string[];
  /** Whether to show the tax-inclusive checkbox on line items. Default false (net pricing). */
  allowTaxInclusive?: boolean;
  /** Budget category tags per line item index */
  budgetCategoriesByLineItem?: Record<number, string[]>;
  /** Called when budget categories change for a line item */
  onBudgetCategoryChange?: (index: number, categoryIds: string[]) => void;
  /** Whether this is an expense form (affects tax UI for SALES_TAX orgs). Default false. */
  isExpense?: boolean;
  /** Opt-in NL WKR per-line classifier (expense/AP forms only — never AR). The select self-gates on flag+jurisdiction. */
  enableWkr?: boolean;
  /** Never seed a tax rate (org default, account default, new lines) — e.g. a foreign-currency payment account. */
  suppressDefaultTax?: boolean;
  /**
   * The document's date (`YYYY-MM-DD`): the rates in force on that day are
   * offered first and are the only defaults and scan matches; the others are
   * offered below them for a deliberate pick (useJurisdictionTaxConfig
   * `asOfDate`). Omitted → today.
   */
  asOfDate?: string | null;
}

// Helper to get effective tax rate for calculations
const getEffectiveTaxRate = (taxRate: TaxRateType): number => {
  // Exempt and "VAT not reclaimable" both carry no deductible VAT: the line
  // is its gross amount (non_reclaimable: VAT included, booked as cost).
  if (taxRate === 'exempt' || taxRate === 'non_reclaimable') {
    return 0;
  }
  return typeof taxRate === 'number' ? taxRate : 0;
};

/**
 * A scanned line holds the receipt's NET amount: "VAT not reclaimable" books
 * the line as cost with no VAT leg, so it is grossed up to net + scanned VAT
 * (once), and any other rate restores the net (lib/taxRateScan.ts). Every
 * writer of a line's rate — the picker, an account's default rate, a new
 * custom rate — calls this first, or a grossed-up price would be taxed again.
 */
const settleScannedAmountForRate = (form: UseFormReturn<FieldValues>, index: number, newRate: unknown) => {
  const change = amountChangeForRate(form.getValues(`line_items.${index}`), newRate);
  if (!change) return;
  // The price is a real edit; the hint fields are form-only.
  for (const [key, fieldValue] of Object.entries(change)) {
    form.setValue(`line_items.${index}.${key}`, fieldValue, { shouldDirty: key === 'unit_price' });
  }
};

// Check if a tax rate requires an exemption reason
const requiresExemptionReason = (taxRate: TaxRateType, rates: TaxRate[]): boolean => {
  if (taxRate === 'exempt' || taxRate === -1) return true;
  // Not an exemption — VAT was charged; it just cannot be deducted.
  if (taxRate === 'non_reclaimable') return false;
  const rateKey = taxRateToSelectValue(taxRate, rates);
  const rateConfig = rates.find(r => r.key === rateKey);
  return rateConfig?.requiresExemptionReason || false;
};

const CUSTOM_RATE_KEY = '__custom__';

// Per-line carbon capture. Ungated: any line with a selected account can attach a
// carbon impact (the "5 cups" general-expense case). An account that IS carbon-
// linked pre-fills its table + pinned dims + unit inside LineItemCarbon.
interface CarbonTrackingRowProps {
  accountId: string;
  index: number;
  carbonData: CarbonData | null | undefined;
  onCarbonDataChange: (index: number, data: CarbonData | null) => void;
}

const CarbonTrackingRow = ({
  accountId,
  index,
  carbonData,
  onCarbonDataChange,
}: CarbonTrackingRowProps) => {
  return (
    // Child rows of a line item are PLAIN INDENTED ROWS: no row border/hover
    // (a full-width background or separator makes the table look broken), no
    // chip background, tight py-0.5 spacing, content indented ml-8. Exemption /
    // carbon / budget / WKR all match; a component that draws its own card
    // (LineItemCarbon) provides its own chrome.
    <TableRow className="border-0 hover:bg-transparent">
      {/* Indent only — LineItemCarbon draws its own card (border-success bg)
          when expanded and a bare button when collapsed; a chip background
          here would double the chrome. */}
      <TableCell colSpan={7} className="p-0 py-0.5">
        <div className="ml-8 mr-2">
          <LineItemCarbon
            accountId={accountId}
            value={carbonData}
            onChange={(data) => onCarbonDataChange(index, data)}
          />
        </div>
      </TableCell>
    </TableRow>
  );
};

/**
 * Whether the row shows "Scanned: N%": a hint exists and the line's rate is
 * not (within the hint's tolerance) that percentage — the scan matched no
 * rate, tied two, or the user picked another rate.
 */
const showScanHint = (hint: number | undefined, computed: boolean | undefined, lineRate: unknown): boolean => {
  if (hint === undefined || !(hint > 0)) return false;
  if (typeof lineRate !== 'number') return true;
  const tolerance = (computed ? COMPUTED_RATE_HINT_TOLERANCE : 0) + 1e-9;
  return Math.abs(hint - lineRate) > tolerance;
};

// Shared tax rate dropdown — used in both SALES_TAX and VAT paths
interface TaxRateDropdownProps {
  index: number;
  form: UseFormReturn<FieldValues>;
  taxConfig: {
    rates: TaxRate[];
    taxColumnLabel: string;
    isConfigured: boolean;
  };
  rates: TaxRate[];
  /** The line's stored `tax_rate_id` — lets the picker show the exact rate the line was posted with. */
  taxRateId?: string;
  /** The scanned percentage (`tax_rate_hint`); shown when it differs from the line's rate. */
  taxRateHint?: number;
  /** The hint was computed from totals — "differs" then means by more than ±0.01. */
  taxRateHintComputed?: boolean;
  /** The line's named rate is not in force on the document date — a warning, never a block. */
  rateNotInForce?: boolean;
  /** A scanned line still waiting for a pick (lib/taxRateScan.ts): the picker shows blank, not the 0% option. */
  scanUnresolved?: boolean;
  disabled: boolean;
  /** Absent = no "Custom rate" entry (a sales-tax purchase line: see the parent). */
  onOpenCustomRate?: (index: number) => void;
}

const TaxRateDropdown = ({
  index,
  form,
  taxConfig,
  rates,
  taxRateId,
  taxRateHint,
  taxRateHintComputed,
  rateNotInForce,
  scanUnresolved,
  disabled,
  onOpenCustomRate,
}: TaxRateDropdownProps) => {
  const { t } = useTranslation('common');
  // In-window rates and the synthetics first; the rates of other dates (a
  // retired rate, a future one) below their own heading, labelled with their
  // window — a deliberate pick for an earlier or later supply.
  const currentRates = rates.filter((rate) => !rate.otherDate);
  const otherDateRates = rates.filter((rate) => rate.otherDate);
  return (
  <FormField
    control={form.control}
    name={`line_items.${index}.tax_rate`}
    render={({ field, fieldState }) => (
      <FormItem className="min-w-0">
        <FormControl>
          <Select
            value={scanUnresolved ? '' : taxRateToSelectValue(field.value, taxConfig.rates, taxRateId)}
            onValueChange={(value) => {
              // Never a pick: Radix reports '' when the value moves to a key whose
              // hidden native <option> hasn't registered yet (options register one
              // render late) — e.g. the render where the org's tax rates arrive.
              // Acting on it would zero a loaded line's rate and id (and dirty the
              // form), or clear a placeholder's flag before the default fill runs.
              // There is no "clear" option, and SelectItem forbids value ''.
              if (value === '') return;
              if (value === CUSTOM_RATE_KEY) {
                onOpenCustomRate?.(index);
                return;
              }
              const newTaxRate = selectValueToTaxRate(value, taxConfig.rates);
              // Every pick confirms the line — "No VAT" on a scanned line
              // included — so the scan-miss block (lib/taxRateScan.ts) clears.
              // Set BEFORE field.onChange: its revalidation reads it.
              form.setValue(`line_items.${index}.tax_rate_confirmed`, true, { shouldDirty: false });
              // "VAT not reclaimable" excludes reverse charge: under reverse
              // charge the buyer accounts for the VAT itself, so it was never
              // charged on the bill. The RC box is disabled while it holds.
              if (newTaxRate === 'non_reclaimable') {
                form.setValue(`line_items.${index}.is_reverse_charge`, false, { shouldDirty: true });
              }
              settleScannedAmountForRate(form, index, newTaxRate);
              field.onChange(newTaxRate);
              const taxRateId = selectValueToTaxRateId(value, taxConfig.rates);
              form.setValue(`line_items.${index}.tax_rate_id`, taxRateId ?? '', {
                shouldDirty: true,
              });
              // A user pick is a real choice — the default-rate fill must not
              // overwrite it if it fires after this.
              form.setValue(`line_items.${index}.tax_rate_unset`, false, { shouldDirty: false });
              if (!requiresExemptionReason(newTaxRate, taxConfig.rates)) {
                form.setValue(`line_items.${index}.exemption_reason`, '', {
                  shouldValidate: true,
                });
                form.setValue(`line_items.${index}.exemption_reason_other`, '', {
                  shouldValidate: true,
                });
              }
            }}
            disabled={disabled || !taxConfig.isConfigured}
          >
            <SelectTrigger
              className={`h-9 ${fieldState.error ? "border-destructive" : rateNotInForce ? "border-warning" : ""}`}
            >
              <SelectValue placeholder={taxConfig.taxColumnLabel} />
            </SelectTrigger>
            <SelectContent>
              {currentRates.map((rate) => (
                /* A retired rate is listed only because a line on this document
                   still points at it: shown so that line reads correctly, but
                   disabled so nothing new is posted against it. */
                <SelectItem key={rate.key} value={rate.key} disabled={rate.isInactive}>
                  {rate.label}
                </SelectItem>
              ))}
              {otherDateRates.length > 0 && (
                <>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className="text-xs font-normal text-muted-foreground">
                      {t('tax.ui.otherDateRates')}
                    </SelectLabel>
                    {otherDateRates.map((rate) => (
                      <SelectItem key={rate.key} value={rate.key}>
                        {rate.label}
                        <span className="ml-1 text-muted-foreground">
                          ({windowLabel(t, rate.otherDate?.from ?? null, rate.otherDate?.to ?? null)})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}
              {onOpenCustomRate && (
                <SelectItem value={CUSTOM_RATE_KEY} className="text-muted-foreground border-t mt-1 pt-1">
                  {t('tax.ui.customRate')}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </FormControl>
        {/* A warning, not a block (warning tone — red is kept for the
            scan-miss block below): VAT follows the supply, so a credit note
            or a late bill for an earlier supply rightly names the old rate. */}
        {rateNotInForce && (
          <p className="text-[10px] leading-tight text-warning">{t('tax.ui.rateNotInForce')}</p>
        )}
        {fieldState.error?.message === SCAN_RATE_REQUIRED && (
          <p className="text-[10px] leading-tight text-destructive">
            {/* Names the line's own 0% option — "No VAT", or the org's DB 0%
                rate (e.g. "BTW 0%") when it has one and no "No VAT" exists —
                and, on a VAT purchase line, "VAT not reclaimable" for VAT
                that was charged but cannot be deducted (foreign VAT). */}
            {(() => {
              const zeroOption = zeroRateOptionLabel(taxConfig.rates) ?? t('tax.ui.noVatPurchase', 'No VAT');
              const notReclaimableOption = notReclaimableOptionLabel(taxConfig.rates);
              return notReclaimableOption
                ? t(
                    SCAN_RATE_REQUIRED,
                    'The scanned rate matches none of your rates — pick a rate, {{zeroOption}} if no VAT was charged, or {{notReclaimableOption}} if VAT was charged that you can\'t reclaim (for example foreign VAT).',
                    { zeroOption, notReclaimableOption },
                  )
                : t(
                    SCAN_RATE_REQUIRED_ZERO_ONLY,
                    'The scanned rate matches none of your rates — pick a rate, or {{zeroOption}} if no VAT was charged.',
                    { zeroOption },
                  );
            })()}
          </p>
        )}
        {/* What the scan read, when the line does not carry it — a figure that
            matched no rate (or tied two) is never snapped to a near rate. */}
        {showScanHint(taxRateHint, taxRateHintComputed, field.value) && (
          <p className="text-[10px] leading-tight text-muted-foreground">
            {t('tax.ui.scannedRate')}{' '}
            <NumberDisplay value={taxRateHint ?? 0} decimals={2} minDecimals={0} suffix="%" />
          </p>
        )}
      </FormItem>
    )}
  />
  );
};

// Props for the memoized line item row
interface LineItemRowProps {
  index: number;
  fieldId: string;
  type: InvoiceLineItemType;
  form: UseFormReturn<FieldValues>;
  accounts?: Account[];
  revenueAccounts?: Account[];
  disabled: boolean;
  taxConfig: {
    rates: TaxRate[];
    taxColumnLabel: string;
    isConfigured: boolean;
    exemptionRegion: string;
    exemptionReasons: Array<{ key: string; label: string; placeholder?: string }>;
    taxSystem: 'VAT' | 'SALES_TAX';
  };
  formatAmount: (amount: number) => string;
  pinnedAccountIds?: string[];
  carbonData: CarbonData | null | undefined;
  onCarbonDataChange?: (index: number, data: CarbonData | null) => void;
  applyAccountDefaultTaxRate: (index: number, selectedAccount: AccountOption | undefined) => void;
  onOpenCustomRate?: (index: number) => void;
  canRemove: boolean;
  onRemove: (index: number) => void;
  allowTaxInclusive: boolean;
  isExpense: boolean;
  budgetCategoryIds?: string[];
  onBudgetCategoryChange?: (categoryIds: string[]) => void;
  /** Opt-in NL WKR per-line classifier (expense/AP forms only — never AR). The select self-gates on flag+jurisdiction. */
  enableWkr?: boolean;
  /** DB rate ids whose window excludes the document date (see InvoiceLineItems). */
  outOfWindowRateIds: ReadonlySet<string>;
}

const LineItemRow = React.memo(({
  index,
  fieldId,
  type,
  form,
  accounts,
  revenueAccounts,
  disabled,
  taxConfig,
  formatAmount,
  pinnedAccountIds,
  carbonData,
  onCarbonDataChange,
  applyAccountDefaultTaxRate,
  onOpenCustomRate,
  canRemove,
  onRemove,
  allowTaxInclusive,
  isExpense,
  budgetCategoryIds,
  onBudgetCategoryChange,
  enableWkr,
  outOfWindowRateIds,
}: LineItemRowProps) => {
  const { t } = useTranslation('common');

  useRenderDebug(`LineItemRow[${index}]`, {
    fieldId, type, accounts, revenueAccounts, disabled,
    taxConfig, formatAmount, pinnedAccountIds,
    carbonData, onCarbonDataChange, applyAccountDefaultTaxRate,
    onOpenCustomRate, canRemove, onRemove,
  });

  // Watch only this specific row's data — avoids re-rendering when other rows change
  const item = useWatch({ control: form.control, name: `line_items.${index}` }) || {
    quantity: 0, unit_price: 0, tax_rate: 0, tax_inclusive: false,
    is_reverse_charge: false, exemption_reason: '', exemption_reason_other: '',
  };

  const qty = item.quantity || 0;
  const enteredUnitPrice = item.unit_price || 0;
  const enteredAmount = qty * enteredUnitPrice;
  const effectiveRate = getEffectiveTaxRate(item.tax_rate);
  const isReverseCharge = item.is_reverse_charge || false;
  const isNotReclaimable = item.tax_rate === 'non_reclaimable';
  const isTaxInclusive = allowTaxInclusive && (item.tax_inclusive || false);
  // Date re-validation: a taxed line naming a rate that is not in force on
  // the document's date (the date moved across a rate change, or the user
  // picked a rate of another date). A WARNING, never a block — VAT follows
  // the supply, so a credit note or a late bill for an earlier supply rightly
  // names the old rate; posting accepts it.
  const rateNotInForce = effectiveRate > 0
    && typeof item.tax_rate_id === 'string'
    && outOfWindowRateIds.has(item.tax_rate_id);
  const scanUnresolved = isUnresolvedScanMiss(item);

  // Use the same rounded line math as LineItemTotals (round per component, then sum)
  // so the row's "Total" cell sums cleanly to the grand total below — SCRUM-141.
  const netUnitPrice = isTaxInclusive && !isReverseCharge && effectiveRate > 0
    ? calculatePreTaxUnitPrice(enteredUnitPrice, effectiveRate)
    : enteredUnitPrice;

  let lineTotalDisplay: number;
  if (!allowTaxInclusive) {
    lineTotalDisplay = calculateLineSubtotal(netUnitPrice, qty);
  } else if (isTaxInclusive || isReverseCharge) {
    lineTotalDisplay = enteredAmount;
  } else {
    lineTotalDisplay = calculateLineTotalWithTax(netUnitPrice, effectiveRate, qty);
  }

  // SALES_TAX expenses offer the reclaimable checkbox only when it can lead
  // somewhere: an active positive purchase rate exists (a sales-tax rate is
  // never INPUT — create_custom_tax_rate refuses it), or the line already
  // carries a rate (a document posted before that rule must still read right).
  const showReclaimable = effectiveRate > 0
    || taxConfig.rates.some((r) => r.numericValue > 0 && !r.isInactive && !r.otherDate);

  // SALES_TAX expenses use the reclaimable checkbox — no exemption reason concept
  const needsExemptionReason = (isExpense && taxConfig.taxSystem === 'SALES_TAX')
    ? false
    : requiresExemptionReason(item.tax_rate, taxConfig.rates);
  const accountId = type === 'ar' ? item.revenue_account_id : item.expense_account_id;

  // Sign the per-line Total so it agrees with the Subtotal/Total footer below
  // (a genuine contra/refund line then shows negative in both). KEEP IN SYNC
  // with the `lineSign` used in LineItemTotals.
  const rowAccounts = type === 'ar' ? revenueAccounts : accounts;
  const lineDisplaySign = getAccountSignForForm(
    rowAccounts?.find((a) => a.id === accountId),
    type,
  );

  return (
    <>
      <TableRow key={`row-${fieldId}`} className="border-b border-border/30">
        {type === 'ar' ? (
          <TableCell className="p-1 w-[250px] max-w-[250px]">
            <FormField
              control={form.control}
              name={`line_items.${index}.revenue_account_id`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <AccountSelect
                      accounts={revenueAccounts || []}
                      value={field.value}
                      onChange={(value, selectedAccount) => {
                        field.onChange(value);
                        applyAccountDefaultTaxRate(index, selectedAccount);
                      }}
                      placeholder="Select account"
                      disabled={disabled}
                      className={fieldState.error ? "border-destructive" : ""}
                      prioritizeCategory="Revenue"
                      pinnedAccountIds={pinnedAccountIds}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </TableCell>
        ) : (
          <TableCell className="p-1 w-[250px] max-w-[250px]">
            <FormField
              control={form.control}
              name={`line_items.${index}.expense_account_id`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <AccountSelect
                      accounts={accounts || []}
                      value={field.value}
                      onChange={(value, selectedAccount) => {
                        field.onChange(value);
                        applyAccountDefaultTaxRate(index, selectedAccount);
                      }}
                      placeholder="Select account"
                      disabled={disabled}
                      className={fieldState.error ? "border-destructive" : ""}
                      prioritizeCategory={["Expenses", "Liabilities"]}
                      pinnedAccountIds={pinnedAccountIds}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </TableCell>
        )}
        <TableCell className="p-1">
          <FormField
            control={form.control}
            name={`line_items.${index}.description`}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="Description"
                    className={fieldState.error ? "border-destructive" : ""}
                    disabled={disabled}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </TableCell>
        <TableCell className="p-1">
          <FormField
            control={form.control}
            name={`line_items.${index}.quantity`}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <NumberInput
                    className={`min-w-0 px-2 ${fieldState.error ? "border-destructive" : ""}`}
                    disabled={disabled}
                    defaultOnBlur={1}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </TableCell>
        <TableCell className="p-1">
          <FormField
            control={form.control}
            name={`line_items.${index}.unit_price`}
            render={({ field, fieldState }) => (
              <FormItem>
                <FormControl>
                  <NumberInput
                    className={`min-w-0 px-2 ${fieldState.error ? "border-destructive" : ""}`}
                    disabled={disabled}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </TableCell>
        {/* Total before Tax — total column always precedes tax */}
        <TableCell className="p-1 text-right font-mono">
          {formatAmount(lineDisplaySign * lineTotalDisplay)}
        </TableCell>
        <TableCell className="p-1">
          {isExpense && taxConfig.taxSystem === 'SALES_TAX' ? (
            /* US SALES_TAX expense: show reclaimable checkbox, reveal dropdown only when checked */
            showReclaimable && (
            <div className="space-y-1">
              <label className="flex items-center gap-2 cursor-pointer py-1">
                <Checkbox
                  checked={effectiveRate > 0}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      form.setValue(`line_items.${index}.tax_rate`, 0, { shouldDirty: true });
                      form.setValue(`line_items.${index}.tax_rate_id`, '', { shouldDirty: true });
                      form.setValue(`line_items.${index}.tax_inclusive`, false, { shouldDirty: true });
                      form.setValue(`line_items.${index}.is_reverse_charge`, false, { shouldDirty: true });
                    } else {
                      // Apply default rate when checking reclaimable; price is gross so always tax-inclusive
                      const defaultRateObj = taxConfig.rates.find(r => r.numericValue > 0 && !r.isInactive && !r.otherDate);
                      if (defaultRateObj) {
                        form.setValue(`line_items.${index}.tax_rate`, defaultRateObj.numericValue, { shouldDirty: true });
                        form.setValue(`line_items.${index}.tax_rate_id`, defaultRateObj.key, { shouldDirty: true });
                      }
                      form.setValue(`line_items.${index}.tax_inclusive`, true, { shouldDirty: true });
                    }
                  }}
                  disabled={disabled}
                  className="h-3.5 w-3.5"
                />
                <span className="text-xs text-muted-foreground select-none">{t('tax.ui.reclaimable')}</span>
              </label>
              {effectiveRate > 0 && (
                <TaxRateDropdown
                  index={index}
                  form={form}
                  taxConfig={taxConfig}
                  rates={taxConfig.rates.filter(r => r.numericValue > 0)}
                  taxRateId={item.tax_rate_id}
                  taxRateHint={item.tax_rate_hint}
                  taxRateHintComputed={item.tax_rate_hint_computed}
                  rateNotInForce={rateNotInForce}
                  scanUnresolved={scanUnresolved}
                  disabled={disabled}
                  onOpenCustomRate={onOpenCustomRate}
                />
              )}
            </div>
            )
          ) : (
            /* VAT orgs or non-expense forms: standard tax dropdown */
            <div className="space-y-1">
              <TaxRateDropdown
                index={index}
                form={form}
                taxConfig={taxConfig}
                rates={taxConfig.rates}
                taxRateId={item.tax_rate_id}
                taxRateHint={item.tax_rate_hint}
                taxRateHintComputed={item.tax_rate_hint_computed}
                rateNotInForce={rateNotInForce}
                scanUnresolved={scanUnresolved}
                disabled={disabled}
                onOpenCustomRate={onOpenCustomRate}
              />
              {/* Reverse charge is a VAT-only concept (cross-border/intra-EU services);
                  it must not appear for US sales-tax orgs. "Incl" stays gated by
                  allowTaxInclusive. Only render the row when something shows. */}
              {(allowTaxInclusive || taxConfig.taxSystem !== 'SALES_TAX') && (
                <div className="flex items-center gap-3 pl-0.5">
                  {allowTaxInclusive && (
                    <FormField
                      control={form.control}
                      name={`line_items.${index}.tax_inclusive`}
                      render={({ field }) => (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <Checkbox
                                checked={field.value || false}
                                onCheckedChange={field.onChange}
                                disabled={disabled}
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-[10px] text-muted-foreground select-none">Incl</span>
                            </label>
                          </TooltipTrigger>
                          <TooltipContent>Tax-inclusive pricing</TooltipContent>
                        </Tooltip>
                      )}
                    />
                  )}
                  {taxConfig.taxSystem !== 'SALES_TAX' && (
                    <FormField
                      control={form.control}
                      name={`line_items.${index}.is_reverse_charge`}
                      render={({ field }) => (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <label className="flex items-center gap-1 cursor-pointer">
                              <Checkbox
                                checked={field.value || false}
                                onCheckedChange={field.onChange}
                                // Not with "VAT not reclaimable": that VAT was
                                // charged on the bill; under reverse charge
                                // none is.
                                disabled={disabled || isNotReclaimable}
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-[10px] text-muted-foreground select-none">RC</span>
                            </label>
                          </TooltipTrigger>
                          <TooltipContent>Reverse charge</TooltipContent>
                        </Tooltip>
                      )}
                    />
                  )}
                  {/* NL WKR toggle — same shape as Incl/RC; checking it
                      reveals the category sub-row below the line (exemption-
                      reason pattern). Self-gates on flag+jurisdiction.
                      Unchecking clears the category so nothing stale rides
                      the payload into nl_wkr_sync_from_lines. */}
                  {enableWkr && (
                    <FormField
                      control={form.control}
                      name={`line_items.${index}.wkr_enabled`}
                      render={({ field }) => (
                        <WkrLineToggle
                          checked={field.value || false}
                          onChange={(checked) => {
                            field.onChange(checked);
                            if (!checked) {
                              form.setValue(`line_items.${index}.wkr_category`, '', {
                                shouldDirty: true,
                              });
                            }
                          }}
                          disabled={disabled}
                        />
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </TableCell>
        <TableCell className="p-1">
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onRemove(index)}
              disabled={disabled}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </TableCell>
      </TableRow>

      {/* "VAT not reclaimable": one muted line saying what it means — the VAT
          is part of the cost, and a VAT charged in error is the supplier's to
          correct, not this line's. No exemption-reason row: it is not an
          exemption. */}
      {isNotReclaimable && (
        <TableRow key={`not-reclaimable-${fieldId}`} className="border-0 hover:bg-transparent">
          <TableCell colSpan={7} className="p-0 py-0.5">
            {/* The table is min-w-max: `w-0 min-w-full` keeps this sentence
                out of the table's width so it wraps instead of widening it. */}
            <div className="w-0 min-w-full">
              <p className="ml-8 mr-2 text-xs text-muted-foreground">
                {t('tax.ui.vatNotReclaimableHint')}
              </p>
            </div>
          </TableCell>
        </TableRow>
      )}

      {needsExemptionReason && (
        <TableRow key={`exemption-${fieldId}`} className="border-0 hover:bg-transparent">
          <TableCell colSpan={7} className="p-0 py-0.5">
            <div className="ml-8 mr-2 flex items-center gap-4">
              {/* Sales invoices must state the exemption ground (VAT Directive
                  art. 226(11), Wet OB art. 35a); on a purchase it is the
                  supplier's duty, so the reason is optional there. */}
              <span
                className={cn(
                  'inline-flex h-8 items-center text-xs whitespace-nowrap',
                  type === 'ar' ? 'text-amber-600 font-medium' : 'text-muted-foreground',
                )}
              >
                {type === 'ar' ? t('tax.ui.exemptionRequired') : t('tax.ui.exemptionOptional')}
              </span>
              <FormField
                control={form.control}
                name={`line_items.${index}.exemption_reason`}
                render={({ field: exemptionField, fieldState }) => (
                  <FormItem className="flex-1 max-w-xs">
                    <FormControl>
                      <Select
                        value={exemptionField.value || ''}
                        onValueChange={exemptionField.onChange}
                        disabled={disabled}
                      >
                        <SelectTrigger
                          className={`h-8 text-xs ${fieldState.error ? "border-destructive" : ""}`}
                        >
                          <SelectValue placeholder={t(`tax.exemptionReasons.${taxConfig.exemptionRegion}.placeholder`)} />
                        </SelectTrigger>
                        <SelectContent>
                          {taxConfig.exemptionReasons.map((reason) => (
                            <SelectItem key={reason.key} value={reason.key}>
                              {reason.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              {item.exemption_reason === 'other' && (
                <FormField
                  control={form.control}
                  name={`line_items.${index}.exemption_reason_other`}
                  render={({ field: otherField, fieldState }) => {
                    const otherReason = taxConfig.exemptionReasons.find(r => r.key === 'other');
                    return (
                      <FormItem className="flex-1 max-w-xs">
                        <FormControl>
                          <Input
                            placeholder={otherReason?.placeholder || t('vat.ui.customPlaceholder')}
                            className={`h-8 text-xs ${fieldState.error ? "border-destructive" : ""}`}
                            disabled={disabled}
                            {...otherField}
                          />
                        </FormControl>
                      </FormItem>
                    );
                  }}
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      )}

      {/* NL WKR category sub-row — exemption-reason pattern: revealed by the
          WKR checkbox in the tax cell. The select self-gates, so a stale
          wkr_enabled on a no-longer-gated org renders an empty row at worst. */}
      {enableWkr && item.wkr_enabled && (
        <TableRow key={`wkr-${fieldId}`} className="border-0 hover:bg-transparent">
          <TableCell colSpan={7} className="p-0 py-0.5">
            <div className="ml-8 mr-2 flex items-center gap-4">
              {/* Label box matches the select height so the two text centers
                  coincide exactly (items-center alone leaves line-box drift). */}
              <span className="inline-flex h-7 items-center text-xs text-muted-foreground font-medium whitespace-nowrap">
                {t('tax.nl.wkr.selectLabel')}
              </span>
              <FormField
                control={form.control}
                name={`line_items.${index}.wkr_category`}
                render={({ field }) => (
                  <FormItem className="flex-1 max-w-xs">
                    <FormControl>
                      <WkrCategorySelect
                        value={field.value || null}
                        onChange={(category) => field.onChange(category ?? '')}
                        disabled={disabled}
                        size="compact"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </TableCell>
        </TableRow>
      )}

      {accountId && onCarbonDataChange && (
        <CarbonTrackingRow
          key={`carbon-${fieldId}`}
          accountId={accountId}
          index={index}
          carbonData={carbonData}
          onCarbonDataChange={onCarbonDataChange}
        />
      )}

      {onBudgetCategoryChange && (
        <TableRow key={`budget-${fieldId}`} className="border-0 hover:bg-transparent">
          <TableCell colSpan={7} className="p-0 py-0.5">
            <div className="ml-8 mr-2">
              <BudgetCategoryPicker
                selectedCategoryIds={budgetCategoryIds ?? []}
                onChange={onBudgetCategoryChange}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
});

LineItemRow.displayName = 'LineItemRow';

// Extracted totals component — uses useWatch internally so the parent doesn't
// re-render on every keystroke. Only this small component re-renders when
// line item values change.
interface LineItemTotalsProps {
  form: UseFormReturn<FieldValues>;
  /** AR or AP — drives the sign on each line via getAccountSignForForm. */
  type: InvoiceLineItemType;
  /** All accounts visible to the picker, used to look up each line's
   *  category and apply the per-line sign to subtotal/tax/grand total. */
  accounts: Account[];
  taxConfig: {
    rates: TaxRate[];
    totalTaxLabel?: string;
    exemptionRegion: string;
    taxSystem: 'VAT' | 'SALES_TAX';
  };
  formatAmount: (amount: number) => string;
  summaryLeftContent?: React.ReactNode;
  onHasReverseCharge?: (hasRC: boolean) => void;
  onTaxNotesChange?: (notes: string) => void;
  allowTaxInclusive: boolean;
}

interface TaxByRateEntry {
  label: string;
  amount: number;
  rate: number;
}

const LineItemTotals = ({
  form,
  type,
  accounts,
  taxConfig,
  formatAmount,
  summaryLeftContent,
  onHasReverseCharge,
  onTaxNotesChange,
  allowTaxInclusive,
}: LineItemTotalsProps) => {
  const { t } = useTranslation('common');
  const lineItems = useWatch({ control: form.control, name: 'line_items' }) as BaseLineItem[] | undefined;

  // Look up account by id once per render so each line can ask its account's
  // sign without an O(N) search per reduce iteration.
  const accountById = useMemo(() => {
    const map = new Map<string, Account>();
    for (const a of accounts) map.set(a.id, a);
    return map;
  }, [accounts]);

  // Per-line sign (+1 add / -1 subtract) based on whether the line's
  // account normal balance matches the form direction. Memoized so the
  // taxByRate useMemo below has a stable dep — re-runs only when accounts
  // or form type change. Falls back to +1 when the account is missing or
  // its category isn't classifiable; assertion lives in the helper tests.
  const lineSign = useCallback(
    (item: BaseLineItem): 1 | -1 => {
      const accountId = type === 'ar'
        ? (item as ARLineItem).revenue_account_id
        : (item as APLineItem).expense_account_id;
      return getAccountSignForForm(accountById.get(accountId ?? '') ?? undefined, type);
    },
    [accountById, type],
  );

  const subtotal = lineItems?.reduce((sum: number, item: BaseLineItem) => {
    const effectiveRate = getEffectiveTaxRate(item.tax_rate);
    const isTaxInclusive = allowTaxInclusive && item.tax_inclusive;
    const unitPrice = isTaxInclusive && !item.is_reverse_charge && effectiveRate > 0
      ? calculatePreTaxUnitPrice(item.unit_price || 0, effectiveRate)
      : (item.unit_price || 0);
    return sum + lineSign(item) * calculateLineSubtotal(unitPrice, item.quantity || 0);
  }, 0) || 0;

  const totalTax = lineItems?.reduce((sum: number, item: BaseLineItem) => {
    if (item.is_reverse_charge) return sum;
    const effectiveRate = getEffectiveTaxRate(item.tax_rate);
    const isTaxInclusive = allowTaxInclusive && item.tax_inclusive;
    const unitPrice = isTaxInclusive && effectiveRate > 0
      ? calculatePreTaxUnitPrice(item.unit_price || 0, effectiveRate)
      : (item.unit_price || 0);
    return sum + lineSign(item) * calculateLineTax(unitPrice, effectiveRate, item.quantity || 0);
  }, 0) || 0;

  // Per-tax-rate breakdown
  const taxByRate = useMemo((): TaxByRateEntry[] => {
    if (!lineItems) return [];
    const groups = new Map<string, TaxByRateEntry>();

    lineItems.forEach((item: BaseLineItem) => {
      if (item.is_reverse_charge) return;
      const effectiveRate = getEffectiveTaxRate(item.tax_rate);
      if (effectiveRate === 0) return;

      const isTaxInclusive = allowTaxInclusive && item.tax_inclusive;
      const unitPrice = isTaxInclusive && effectiveRate > 0
        ? calculatePreTaxUnitPrice(item.unit_price || 0, effectiveRate)
        : (item.unit_price || 0);
      const lineTax = lineSign(item) * calculateLineTax(unitPrice, effectiveRate, item.quantity || 0);

      // Resolve the display label first, then group on (label, rate) — NOT on
      // tax_rate_id. The same percentage reaches a line by two routes: picked
      // from a DB rate (tax_rate_id = its UUID) or from a jurisdiction-config
      // rate (selectValueToTaxRateId returns undefined for non-UUID keys). Two
      // 21% lines that arrived by different routes are one VAT rate to the
      // reader and to the filing, so keying on the id would print "BTW 21%"
      // twice — and collide the React key below, which is the label.
      const rateConfig = item.tax_rate_id
        ? taxConfig.rates.find(r => r.key === item.tax_rate_id)
        : uniqueRateAtPercentage(taxConfig.rates, effectiveRate);
      const label = rateConfig?.label || `${effectiveRate}%`;

      const groupKey = `${label}|${effectiveRate}`;
      const existing = groups.get(groupKey);
      if (existing) {
        existing.amount += lineTax;
      } else {
        groups.set(groupKey, { label, amount: lineTax, rate: effectiveRate });
      }
    });

    // Sort by rate ascending, matching the approved NewSale board (BTW 9%
    // above BTW 21%); label breaks ties so two distinct labels at the same
    // percentage keep a stable row order across renders.
    return Array.from(groups.values())
      .sort((a, b) => a.rate - b.rate || a.label.localeCompare(b.label));
  }, [lineItems, taxConfig.rates, allowTaxInclusive, lineSign]);

  const grandTotal = subtotal + totalTax;
  const hasReverseCharge = lineItems?.some((item: BaseLineItem) => item.is_reverse_charge) || false;

  // Self-assessed VAT on reverse-charge purchase lines. Informational only —
  // it is NOT part of the document total (the vendor is paid net); the ledger
  // posts it as an offsetting DR Input VAT / CR Output VAT pair. RC lines are
  // always net-priced (the DB generated columns ignore tax_inclusive for RC),
  // so the raw unit price is the base. AR is excluded: on a sale under
  // reverse charge the customer accounts for the VAT, not us.
  const selfAssessedVat = type !== 'ar' && taxConfig.taxSystem !== 'SALES_TAX'
    ? lineItems?.reduce((sum: number, item: BaseLineItem) => {
        if (!item.is_reverse_charge) return sum;
        const effectiveRate = getEffectiveTaxRate(item.tax_rate);
        if (effectiveRate <= 0) return sum;
        return sum + lineSign(item) * calculateLineTax(item.unit_price || 0, effectiveRate, item.quantity || 0);
      }, 0) || 0
    : 0;

  useEffect(() => {
    onHasReverseCharge?.(hasReverseCharge);
  }, [hasReverseCharge, onHasReverseCharge]);

  useEffect(() => {
    if (!onTaxNotesChange) return;
    const notes: string[] = [];
    if (hasReverseCharge) {
      notes.push(t('tax.reverseCharge.invoiceText'));
    }
    lineItems?.forEach((item: BaseLineItem) => {
      if (item.exemption_reason && item.exemption_reason !== 'other') {
        const invoiceText = t(`tax.exemptionReasons.${taxConfig.exemptionRegion}.${item.exemption_reason}.invoiceText`, { defaultValue: '' });
        if (invoiceText && !notes.includes(invoiceText)) {
          notes.push(invoiceText);
        }
      } else if (item.exemption_reason === 'other' && item.exemption_reason_other) {
        if (!notes.includes(item.exemption_reason_other)) {
          notes.push(item.exemption_reason_other);
        }
      }
    });
    onTaxNotesChange(notes.join('\n'));
  }, [lineItems, hasReverseCharge, onTaxNotesChange, t, taxConfig.exemptionRegion]);

  return (
    <div className="mt-4 flex justify-between gap-8">
      <div className="w-1/2 max-w-md">
        {summaryLeftContent}
      </div>
      <div className="w-64 space-y-2 flex-shrink-0 mr-[40px]">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{t('tax.ui.taxBreakdown.subtotal')}:</span>
          <span className="font-mono">{formatAmount(subtotal)}</span>
        </div>
        {/* US sales tax: always surface the Sales Tax line on SALES invoices (AR),
            even at $0.00, so it's clear tax was considered (no-nexus / non-taxable
            / exempt sales). Not on AP bills or expenses, where purchase tax is
            embedded in the gross price and a "$0.00" line would mislead. VAT orgs
            keep the original behavior of hiding a zero line. */}
        {(Math.abs(totalTax) > 0.005 || (taxConfig.taxSystem === 'SALES_TAX' && type === 'ar')) && (
          taxByRate.length > 1 ? (
            taxByRate.map((entry) => (
              <div key={`${entry.rate}|${entry.label}`} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{entry.label}:</span>
                <span className="font-mono">{formatAmount(entry.amount)}</span>
              </div>
            ))
          ) : (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{taxConfig.totalTaxLabel || t('vat.ui.totalVat')}:</span>
              <span className="font-mono">{formatAmount(totalTax)}</span>
            </div>
          )
        )}
        {Math.abs(selfAssessedVat) > 0.005 && (
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{t('tax.ui.selfAssessedVat')}:</span>
            <span className="font-mono">{formatAmount(selfAssessedVat)}</span>
          </div>
        )}
        {hasReverseCharge && (
          <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
            <Info className="h-3 w-3" />
            <span>{t(type === 'ar' ? 'tax.ui.reverseChargeNotice.title' : 'tax.ui.reverseChargeNotice.purchaseTitle')}</span>
          </div>
        )}
        <div className="flex justify-between text-lg font-semibold border-t border-border pt-2">
          <span className="text-primary">{t('tax.ui.taxBreakdown.total')}:</span>
          <span className="font-mono text-primary">{formatAmount(grandTotal)}</span>
        </div>
      </div>
    </div>
  );
};

LineItemTotals.displayName = 'LineItemTotals';

// Sentinel for the totals component when neither accounts side is provided.
// Module-level so the reference is stable across renders — prevents
// `accountById` inside LineItemTotals from rebuilding every render when
// callers pass no accounts (e.g. tests that don't exercise the picker).
const EMPTY_ACCOUNTS: AccountOption[] = [];

export const InvoiceLineItems = ({
  type,
  form,
  accounts,
  revenueAccounts,
  disabled = false,
  currency,
  carbonDataByLineItem,
  onCarbonDataChange,
  summaryLeftContent,
  onHasReverseCharge,
  onTaxNotesChange,
  pinnedAccountIds,
  allowTaxInclusive = false,
  isExpense = false,
  budgetCategoriesByLineItem,
  onBudgetCategoryChange,
  enableWkr = false,
  suppressDefaultTax = false,
  asOfDate,
}: InvoiceLineItemsProps) => {
  const { t } = useTranslation('common');
  const orgBasePath = useOrgBasePath();
  const { selectedOrganization } = useOrganization();

  useRenderDebug('InvoiceLineItems', {
    type, form, accounts, revenueAccounts, disabled, currency,
    carbonDataByLineItem, onCarbonDataChange, summaryLeftContent,
    onHasReverseCharge, onTaxNotesChange, pinnedAccountIds,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'line_items',
  });

  // Get jurisdiction-aware tax configuration, filtered by direction.
  // `fields` carries each line's values as of the last array change (mount,
  // reset, append, remove) — exactly when a loaded document's rate ids arrive
  // — so reading tax_rate_id off it keeps a retired rate visible on the lines
  // that were posted with it, without watching every keystroke.
  const direction: TaxDirection = type === 'ar' ? 'OUTPUT' : 'INPUT';
  const referencedRateIds = useMemo(
    () => fields
      .map((f) => (f as { tax_rate_id?: string | null }).tax_rate_id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
    [fields],
  );
  // A loaded line stored as exempt: a VAT purchase picker no longer offers
  // "Exempt", so the hook lists it disabled for this document only — the line
  // keeps showing (and saving) its stored value until the user changes it.
  const hasLegacyExempt = useMemo(
    () => fields.some((f) => {
      const rate = (f as { tax_rate?: unknown }).tax_rate;
      return rate === 'exempt' || rate === -1;
    }),
    [fields],
  );
  const taxConfig = useJurisdictionTaxConfig(direction, { referencedRateIds, asOfDate, hasLegacyExempt });
  const isSalesTaxExpense = isExpense && taxConfig.taxSystem === 'SALES_TAX';
  // Lines start at 0 instead of the org default (a SALES_TAX expense is not
  // reclaimable; a foreign-currency payment account carries no tax).
  const skipDefaultRate = isSalesTaxExpense || suppressDefaultTax;

  // Custom rate dialog state
  const [customRateOpen, setCustomRateOpen] = useState(false);
  const [customRateLineIndex, setCustomRateLineIndex] = useState<number | null>(null);

  // Helper to format amounts using account currency if provided, fallback to org currency
  const formatAmount = useCallback((amount: number): string => {
    if (currency) {
      const locale = getCurrencyLocale(currency);
      return formatCurrency(amount, currency, locale);
    }
    return formatOrganizationCurrency(amount, selectedOrganization);
  }, [currency, selectedOrganization]);

  // The document's day the rates are read on (asOfDate, else today).
  const asOfDay = taxConfig.asOfDay ?? formatLocalISODate(new Date());
  const rateRows = taxConfig.rateRows;

  // DB rate ids not in force on the document's day — the date re-validation
  // set each row reads (a line naming one shows the "not in force" warning).
  const outOfWindowRateIds = useMemo(
    () => new Set((rateRows ?? []).filter((row) => !isRateEffectiveOn(row, asOfDay)).map((row) => row.id)),
    [rateRows, asOfDay],
  );

  // Auto-fill tax rate from an account's default_tax_rate_id when account is selected.
  // The stored default is walked along its lineage to the rate in force on the
  // document's day (a 21% default on a document dated after a 21% → 23%
  // change applies 23%); nothing in force → nothing applied, never the org's
  // standard default (that would be the wrong tier). The result must be
  // listed in the direction-filtered rates and active.
  const applyAccountDefaultTaxRate = useCallback((index: number, selectedAccount: AccountOption | undefined) => {
    if (suppressDefaultTax || !selectedAccount?.default_tax_rate_id) return;

    const inForce = rateInForce(rateRows, selectedAccount.default_tax_rate_id, asOfDay);
    const match = inForce ? taxConfig.rates.find(r => r.key === inForce.id) : undefined;
    if (match && !match.isInactive) {
      settleScannedAmountForRate(form, index, match.numericValue);
      form.setValue(`line_items.${index}.tax_rate`, match.numericValue, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      form.setValue(`line_items.${index}.tax_rate_id`, match.key, {
        shouldDirty: true,
      });
      // The account's rate is a real choice — never overwrite it with the org default.
      form.setValue(`line_items.${index}.tax_rate_unset`, false, { shouldDirty: false });
    }
  }, [taxConfig.rates, form, suppressDefaultTax, rateRows, asOfDay]);

  // The one fill pass, once taxConfig loads. Every line form (expense, AP, AR,
  // channel sale, recurring-template edit) runs under it, so a line reaches
  // the api naming its rate however it was built:
  //
  //  - A **placeholder** (flagged `tax_rate_unset` by the code that created it
  //    — form defaultValues, an empty-document fallback, an import or scan
  //    mapper; its `tax_rate: 0` only stands in for "not chosen yet"):
  //      · with a scan hint (`tax_rate_hint` > 0) → the ONE rate at the hint's
  //        percentage (a computed hint within ±0.01); no unique match → the
  //        line stays at 0% with no default, and the row shows "Scanned: N%"
  //        — never snapped to a near rate;
  //      · without a hint → the org default, as before.
  //    Both respect `skipDefaultRate` (a SALES_TAX expense is not reclaimable;
  //    a foreign-currency payment carries no tax). Every flag is cleared.
  //  - A **loaded** line with `tax_rate > 0` and no `tax_rate_id` (stored
  //    before every line named its rate) adopts the ONE rate at its
  //    percentage, without dirtying the form; on a tie it stays blank and the
  //    user picks (the api refuses an id-less taxed line). A saved 0% line is
  //    never touched. Transitional: stored lines carry their id once the
  //    expense read returns it and the backfill has run.
  //
  // The effect re-runs whenever `fields` changes, so blank lines from a reset
  // ("Save & new", post-submit) are filled too. The write is deferred to a
  // macrotask: when taxConfig is already cached at mount (e.g. reopening the
  // modal), this effect fires on the first commit while useFieldArray is still
  // hydrating line_items from defaultValues, and a synchronous setValue is
  // clobbered by that hydration. A macrotask runs after React has flushed the
  // commit + hydration, so the write sticks. Writing a leaf (`line_items.N.x`)
  // never changes `fields`, so the effect cannot loop on its own writes; the
  // timer is cleared by the effect's cleanup, so a dep-change re-run or a
  // StrictMode remount reschedules rather than losing the fill.
  useEffect(() => {
    if (!taxConfig.isConfigured || !fields.length) return;
    const orgDefault = skipDefaultRate
      ? null
      : taxConfig.rates.find(r => r.key === taxConfig.defaultRate) ?? null;
    const setRate = (index: number, rate: TaxRate) => {
      form.setValue(`line_items.${index}.tax_rate`, rate.numericValue, { shouldDirty: false });
      const rateId = taxRateIdOf(rate);
      if (rateId) form.setValue(`line_items.${index}.tax_rate_id`, rateId, { shouldDirty: false });
    };

    const timer = setTimeout(() => {
      form.getValues('line_items').forEach((item: BaseLineItem | undefined, index: number) => {
        if (!item) return;
        if (item.tax_rate_unset === true) {
          const hint = item.tax_rate_hint ?? 0;
          if (!skipDefaultRate) {
            if (hint > 0) {
              const tolerance = item.tax_rate_hint_computed ? COMPUTED_RATE_HINT_TOLERANCE : 0;
              const scanned = uniqueRateAtPercentage(taxConfig.rates, hint, tolerance);
              // No unique match → the line stays at 0% and blocks submit until
              // the user picks (lib/taxRateScan.ts).
              if (scanned) setRate(index, scanned);
            } else if (orgDefault) {
              setRate(index, orgDefault);
            }
          } else if (hint > 0) {
            // This form never seeds a rate (a sales-tax expense is not
            // reclaimable; a foreign-currency payment carries no tax): its 0%
            // IS the answer, so a scanned hint must not block the save.
            form.setValue(`line_items.${index}.tax_rate_confirmed`, true, { shouldDirty: false });
          }
          form.setValue(`line_items.${index}.tax_rate_unset`, false, { shouldDirty: false });
          return;
        }
        if (typeof item.tax_rate === 'number' && item.tax_rate > 0 && !item.tax_rate_id) {
          const stored = uniqueRateAtPercentage(taxConfig.rates, item.tax_rate);
          const storedId = taxRateIdOf(stored);
          if (storedId) form.setValue(`line_items.${index}.tax_rate_id`, storedId, { shouldDirty: false });
        }
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [taxConfig.isConfigured, taxConfig.defaultRate, taxConfig.rates, fields, form, skipDefaultRate]);

  const handleAddLineItem = useCallback(() => {
    const defaultRateObj = skipDefaultRate
      ? null
      : taxConfig.rates.find(r => r.key === taxConfig.defaultRate);
    const taxRateValue: TaxRateType = defaultRateObj?.numericValue ?? 0;
    const taxRateId = taxRateIdOf(defaultRateObj) ?? '';

    const baseLineItem = {
      description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: taxRateValue,
      tax_rate_id: taxRateId,
      tax_inclusive: false,
      is_reverse_charge: false,
      exemption_reason: '',
      exemption_reason_other: '',
      // Before config loads there is no default to apply yet: mark the 0 as a
      // placeholder so the fill effect sets it once config arrives.
      tax_rate_unset: !taxConfig.isConfigured,
    };

    if (type === 'ar') {
      append({ revenue_account_id: '', ...baseLineItem });
    } else {
      append({ expense_account_id: '', ...baseLineItem });
    }
  }, [append, type, taxConfig.defaultRate, taxConfig.rates, taxConfig.isConfigured, skipDefaultRate]);

  // Stable callbacks for LineItemRow
  const handleOpenCustomRate = useCallback((index: number) => {
    setCustomRateLineIndex(index);
    setCustomRateOpen(true);
  }, []);

  // Auto-select a rate created from the line's "Custom rate…" option.
  const handleCustomRateCreated = useCallback((newRateId: string, rate: number) => {
    if (customRateLineIndex === null) return;
    settleScannedAmountForRate(form, customRateLineIndex, rate);
    form.setValue(`line_items.${customRateLineIndex}.tax_rate`, rate, {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue(`line_items.${customRateLineIndex}.tax_rate_id`, newRateId, { shouldDirty: true });
    // A real pick, like the dropdown and account-default paths.
    form.setValue(`line_items.${customRateLineIndex}.tax_rate_unset`, false, { shouldDirty: false });
  }, [customRateLineIndex, form]);

  const handleRemoveItem = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  // Resolve which accounts list is in play (AR uses revenue, AP/expense
  // uses accounts), with a stable EMPTY_ACCOUNTS sentinel when neither is
  // provided — keeps `accountById` inside LineItemTotals from rebuilding.
  const totalsAccounts = useMemo<AccountOption[]>(
    () => (type === 'ar' ? revenueAccounts : accounts) ?? EMPTY_ACCOUNTS,
    [type, revenueAccounts, accounts],
  );

  // Memoize taxConfig shape for LineItemRow to prevent unnecessary re-renders
  const stableTaxConfig = useMemo(() => ({
    rates: taxConfig.rates,
    taxColumnLabel: taxConfig.taxColumnLabel,
    totalTaxLabel: taxConfig.totalTaxLabel,
    isConfigured: taxConfig.isConfigured,
    exemptionRegion: taxConfig.exemptionRegion,
    exemptionReasons: taxConfig.exemptionReasons,
    taxSystem: taxConfig.taxSystem,
  }), [taxConfig.rates, taxConfig.taxColumnLabel, taxConfig.totalTaxLabel, taxConfig.isConfigured, taxConfig.exemptionRegion, taxConfig.exemptionReasons, taxConfig.taxSystem]);

  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        Product or Service
      </h3>

      <div className="overflow-x-auto">
        <TooltipProvider delayDuration={300}>
          <Table className="text-sm">
            <TableHeader className="bg-releve-gray-light">
              <TableRow className="border-b border-border/50">
                {type === 'ar' && (
                  <TableHead className="w-[250px]">Product/Service *</TableHead>
                )}
                {type === 'ap' && (
                  <TableHead className="w-[250px]">Expense Account *</TableHead>
                )}
                <TableHead className="min-w-[180px]">Description</TableHead>
                <TableHead className="w-[70px]">Qty</TableHead>
                <TableHead className="w-[90px]">
                  {isSalesTaxExpense ? t('tax.ui.columns.priceGross') : t('tax.ui.columns.price')}
                </TableHead>
                <TableHead className="w-[100px] text-right">
                  {isSalesTaxExpense
                    ? t('tax.ui.taxBreakdown.total')
                    : allowTaxInclusive
                      ? (taxConfig.taxSystem === 'SALES_TAX' ? t('tax.ui.columns.totalNet') : t('tax.ui.columns.totalIncl'))
                      : t('tax.ui.columns.totalNet')}
                </TableHead>
                <TableHead className="w-[130px]">
                  {isSalesTaxExpense
                    ? t('tax.ui.taxColumn.salesTax')
                    : (taxConfig.taxColumnLabel || t('tax.ui.taxColumn.vat'))}
                </TableHead>
                <TableHead className="w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field, index) => (
                <LineItemRow
                  key={field.id}
                  index={index}
                  fieldId={field.id}
                  type={type}
                  form={form}
                  accounts={accounts}
                  revenueAccounts={revenueAccounts}
                  disabled={disabled}
                  taxConfig={stableTaxConfig}
                  formatAmount={formatAmount}
                  pinnedAccountIds={pinnedAccountIds}
                  carbonData={carbonDataByLineItem?.[index]}
                  onCarbonDataChange={onCarbonDataChange}
                  applyAccountDefaultTaxRate={applyAccountDefaultTaxRate}
                  // A sales-tax rate is sales-side only (create_custom_tax_rate
                  // refuses INPUT): purchase sales tax is part of the cost.
                  onOpenCustomRate={direction === 'INPUT' && taxConfig.taxSystem === 'SALES_TAX' ? undefined : handleOpenCustomRate}
                  canRemove={fields.length > 1}
                  onRemove={handleRemoveItem}
                  allowTaxInclusive={allowTaxInclusive}
                  isExpense={isExpense}
                  budgetCategoryIds={budgetCategoriesByLineItem?.[index]}
                  onBudgetCategoryChange={onBudgetCategoryChange ? (ids) => onBudgetCategoryChange(index, ids) : undefined}
                  enableWkr={enableWkr}
                  outOfWindowRateIds={outOfWindowRateIds}
                />
              ))}
            </TableBody>
          </Table>
        </TooltipProvider>
      </div>

      {/* Full-width Add Line Item button */}
      <Button
        type="button"
        variant="outline"
        className="w-full mt-3 border-dashed border-2 text-muted-foreground hover:text-accent-foreground hover:border-solid"
        onClick={handleAddLineItem}
        disabled={disabled}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Line Item
      </Button>

      {/* Country Required Warning */}
      {taxConfig.error === 'country_required' && (
        <Alert variant="destructive" className="mt-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex items-center gap-2">
            {t('tax.ui.countryRequired')}
            <Link to={`${orgBasePath}/settings/organization`} className="underline font-medium">
              {t('settings')}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {/* Totals Summary — isolated component so parent doesn't re-render on value changes */}
      <LineItemTotals
        form={form}
        type={type}
        accounts={totalsAccounts}
        taxConfig={stableTaxConfig}
        formatAmount={formatAmount}
        summaryLeftContent={summaryLeftContent}
        onHasReverseCharge={onHasReverseCharge}
        onTaxNotesChange={onTaxNotesChange}
        allowTaxInclusive={allowTaxInclusive}
      />

      {/* Custom Rate Dialog — conditionally mounted (it reads the chart of accounts) */}
      {customRateOpen && (
        <LineItemCustomRateDialog
          onOpenChange={setCustomRateOpen}
          organizationId={selectedOrganization?.id}
          direction={direction}
          taxSystem={taxConfig.taxSystem}
          onCreated={handleCustomRateCreated}
        />
      )}
    </div>
  );
};
